import {
  DrawerContent,
  LoadingCircle,
  Button,
  ButtonVariant,
  Checkbox,
  DrawerActions,
} from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";

import { opbApi } from "@/api/opb/opbApi";
import { Account, AccountSignature, Location } from "@/api/opb/opbTypes";

import { useEffect, useState } from "react";
import { PaymentConfirmationDialog as ConfirmationDialog } from "./PaymentConfirmationDialog";
import { PaymentDrawerTitle as DrawerTitle } from "./PaymentDrawerTitle";
import { PaymentDrawer as Drawer } from "./PaymentDrawer";
import { PaymentMethodSchemaType } from "../schemas/PaymentMethodSchema";
import { ZuoraPaymentPage } from "./ZuoraPaymentPage";

import { usePaymentSetup } from "../usePaymentSetup";
import { CardFeeNotice } from "./CardFeeNotice";

interface Add3dsCreditCardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  accountSignature?: AccountSignature;
  account?: Account;
  accountLocation?: Location;
  isFirstPaymentMethod?: boolean;
  redirectUrl?: string;
  callbackData?: object;
}

const defaultFormData: PaymentMethodSchemaType = {
  cardNumber: "",
  expirationDate: "",
  cvv: "",
  field_creditCardHolderName: "",
  field_passthrough4: "", // nickname
  field_passthrough5: "", // redirect url
  field_passthrough6: "", // callback data
};

export function Add3dsCreditCardDrawer({
  isOpen,
  onClose,
  isLoading = false,
  accountSignature,
  account,
  accountLocation,
  isFirstPaymentMethod = false,
  redirectUrl,
  callbackData,
}: Add3dsCreditCardDrawerProps) {
  const { t } = useTranslation("paymentMethods");

  const [isDefaultPaymentChecked, setIsDefaultPaymentChecked] =
    useState(isFirstPaymentMethod);
  const [refreshedAccountSignature, setRefreshedAccountSignature] =
    useState(accountSignature);

  useEffect(() => {
    setIsDefaultPaymentChecked(isFirstPaymentMethod);
  }, [isFirstPaymentMethod]);

  useEffect(() => {
    setRefreshedAccountSignature(accountSignature);
  }, [accountSignature]);

  const [defaultConfirmationOpen, setDefaultConfirmationOpen] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureError, setSignatureError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const setup = usePaymentSetup({
    isOpen,
    isDirty: true,
    isValid: false,
    onClose,
    onReset: () => setIsDefaultPaymentChecked(isFirstPaymentMethod),
  });
  useEffect(() => {
    if (!isOpen || account == null) return undefined;
    let cancelled = false;
    setSignatureLoading(true);
    setSignatureError(false);
    opbApi
      .createAccountSignature(account._id, "3ds-credit-card")
      .then((signature) => {
        if (!cancelled) setRefreshedAccountSignature(signature);
      })
      .catch(() => {
        if (!cancelled) setSignatureError(true);
      })
      .finally(() => {
        if (!cancelled) setSignatureLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isDefaultPaymentChecked, isOpen, account?._id, attempt]);

  return (
    <>
      <Drawer className="z-10" isOpen={isOpen} onClose={setup.requestClose}>
        {setup.discardDialog}
        <DrawerTitle
          className="pb-4"
          title={t("Add a new card")}
          onClose={setup.requestClose}
        />
        <DrawerContent>
          <CardFeeNotice location={accountLocation} />
          {signatureError && (
            <div role="alert">
              <p>{t("We couldn’t load the payment form. Please try again.")}</p>
              <Button
                type="button"
                variant={ButtonVariant.Outline}
                onClick={() => setAttempt((value) => value + 1)}
              >
                {t("Try again")}
              </Button>
            </div>
          )}
          {(isLoading || signatureLoading) && (
            <div className="payment-loading" role="status">
              <LoadingCircle spinnerSize={30} spinnerColor="#013e3f" />
            </div>
          )}
          {!isLoading &&
            !signatureLoading &&
            !signatureError &&
            refreshedAccountSignature != null && (
              <ZuoraPaymentPage
                accountSignature={refreshedAccountSignature}
                account={account}
                isDefaultPaymentChecked={isDefaultPaymentChecked}
                redirectUrl={redirectUrl}
                callbackData={callbackData}
                defaultFormData={defaultFormData}
              />
            )}
        </DrawerContent>
        <DrawerActions>
          <div className="payment-editor-footer">
            <Checkbox
              label={t("Set as default payment method")}
              checked={isDefaultPaymentChecked}
              isDisabled={isFirstPaymentMethod || signatureLoading}
              onChange={() => {
                if (!isDefaultPaymentChecked && account?.autopay)
                  setDefaultConfirmationOpen(true);
                else setIsDefaultPaymentChecked((value) => !value);
              }}
              subtitle={t(
                "Used for Auto-Pay when it is on. This does not turn Auto-Pay on."
              )}
            />
            <Button
              type="button"
              variant={ButtonVariant.Outline}
              onClick={setup.requestClose}
            >
              {t("Cancel")}
            </Button>
          </div>
        </DrawerActions>
      </Drawer>
      <ConfirmationDialog
        isOpen={defaultConfirmationOpen}
        title={t("Use this method for Auto-Pay?")}
        description={
          <>
            <p>
              {t(
                "Future automatic payments will use this method after it has been added."
              )}
            </p>
            <CardFeeNotice location={accountLocation} />
          </>
        }
        cancelLabel={t("Keep editing")}
        confirmLabel={t("Use as default")}
        onClose={() => setDefaultConfirmationOpen(false)}
        onCancel={() => setDefaultConfirmationOpen(false)}
        onConfirm={() => {
          setIsDefaultPaymentChecked(true);
          setDefaultConfirmationOpen(false);
        }}
      />
    </>
  );
}
